'use client'

import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { MdPerson, MdSave, MdArrowBack } from 'react-icons/md'
import {
    getAdmission,
    createAdmission,
    updateAdmission,
    getUnits,
    getAvailableBeds,
    CreateAdmissionData,
    AdmissionType,
    HospitalUnit,
    Bed,
    ADMISSION_TYPE_LABELS,
} from '@/lib/api/hospital'
import { HospitalSelector } from '@/components/HospitalSelector'
import { useHospitals } from '@/hooks/useHospitals'

export default function AdmissionFormPage() {
    const navigate = useNavigate()
    const { id } = useParams()
    const isEditing = Boolean(id)

    const [loading, setLoading] = useState(false)
    const [saving, setSaving] = useState(false)
    const [units, setUnits] = useState<HospitalUnit[]>([])
    const [beds, setBeds] = useState<Bed[]>([])
    const { hospitals, loading: loadingHospitals, selectedHospitalId, setSelected } = useHospitals()
    const [formData, setFormData] = useState<CreateAdmissionData>({
        hospitalId: 0,
        clientId: 0,
        unitId: 0,
        bedId: undefined,
        admissionType: 'ELECTIVE',
        admissionDate: new Date().toISOString().split('T')[0],
        expectedDischargeDate: '',
        admissionDiagnosis: '',
        cid10Code: '',
        notes: '',
        requiresIsolation: false,
        isolationReason: '',
        allergies: '',
        specialNeeds: '',
        companionName: '',
        companionPhone: '',
        companionRelation: '',
    })

    useEffect(() => {
        if (isEditing && id) {
            loadAdmission(parseInt(id))
        }
    }, [id, isEditing])

    // Auto-preencher hospitalId quando muda o contexto
    useEffect(() => {
        if (!isEditing && selectedHospitalId && formData.hospitalId !== selectedHospitalId) {
            setFormData(prev => ({ ...prev, hospitalId: selectedHospitalId, unitId: 0, bedId: undefined }))
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedHospitalId, isEditing])

    // Recarregar units quando o hospital muda
    useEffect(() => {
        if (formData.hospitalId) {
            loadUnits(formData.hospitalId)
        }
    }, [formData.hospitalId])

    useEffect(() => {
        if (formData.unitId) {
            loadBeds(formData.unitId)
        }
    }, [formData.unitId])

    const loadUnits = async (hospitalId: number) => {
        try {
            const response = await getUnits(undefined, hospitalId)
            if (response.success) {
                setUnits(response.result.filter(u => u.active))
            }
        } catch (error) {
            console.error('Error loading units:', error)
        }
    }

    const loadBeds = async (unitId: number) => {
        try {
            const response = await getAvailableBeds(unitId)
            if (response.success) {
                setBeds(response.result)
            }
        } catch (error) {
            console.error('Error loading beds:', error)
        }
    }

    const loadAdmission = async (admissionId: number) => {
        setLoading(true)
        try {
            const response = await getAdmission(admissionId)
            if (response.success) {
                const adm = response.result
                setFormData({
                    hospitalId: adm.hospitalId,
                    clientId: adm.clientId,
                    unitId: adm.unitId,
                    bedId: adm.bedId,
                    admissionType: adm.admissionType,
                    admissionDate: adm.admissionDate.split('T')[0],
                    expectedDischargeDate: adm.expectedDischargeDate?.split('T')[0] || '',
                    admissionDiagnosis: adm.admissionDiagnosis || '',
                    cid10Code: adm.cid10Code || '',
                    notes: adm.notes || '',
                    requiresIsolation: adm.requiresIsolation,
                    isolationReason: adm.isolationReason || '',
                    allergies: adm.allergies || '',
                    specialNeeds: adm.specialNeeds || '',
                    companionName: adm.companionName || '',
                    companionPhone: adm.companionPhone || '',
                    companionRelation: adm.companionRelation || '',
                })
            } else {
                toast.error(response.error || 'Erro ao carregar internação')
                navigate('/hospital/admissions')
            }
        } catch (error) {
            toast.error('Erro ao carregar internação')
            navigate('/hospital/admissions')
        } finally {
            setLoading(false)
        }
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!formData.hospitalId) {
            toast.error('Selecione um hospital')
            return
        }

        if (!formData.clientId) {
            toast.error('Selecione um paciente')
            return
        }

        if (!formData.unitId) {
            toast.error('Selecione uma unidade')
            return
        }

        setSaving(true)
        try {
            const data = {
                ...formData,
                expectedDischargeDate: formData.expectedDischargeDate || undefined,
                admissionDiagnosis: formData.admissionDiagnosis || undefined,
                cid10Code: formData.cid10Code || undefined,
                notes: formData.notes || undefined,
                isolationReason: formData.isolationReason || undefined,
                allergies: formData.allergies || undefined,
                specialNeeds: formData.specialNeeds || undefined,
                companionName: formData.companionName || undefined,
                companionPhone: formData.companionPhone || undefined,
                companionRelation: formData.companionRelation || undefined,
            }

            let response
            if (isEditing && id) {
                response = await updateAdmission(parseInt(id), data)
            } else {
                response = await createAdmission(data)
            }

            if (response.success) {
                toast.success(
                    isEditing
                        ? 'Internação atualizada com sucesso'
                        : 'Internação criada com sucesso'
                )
                navigate('/hospital/admissions')
            } else {
                toast.error(response.error || 'Erro ao salvar internação')
            }
        } catch (error) {
            toast.error('Erro ao salvar internação')
        } finally {
            setSaving(false)
        }
    }

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">Carregando internação...</p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => navigate('/hospital/admissions')}
                        className="btn btn-ghost btn-sm"
                    >
                        <MdArrowBack className="text-xl" />
                    </button>
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-secondary to-secondary/70 flex items-center justify-center shadow-lg">
                        <MdPerson className="text-2xl text-white" />
                    </div>
                    <div>
                        <h1 className="sm:text-lg lg:text-2xl font-bold text-base-content">
                            {isEditing ? 'Editar Internação' : 'Nova Internação'}
                        </h1>
                        <p className="text-sm text-base-content/60">
                            {isEditing
                                ? 'Atualize os dados da internação'
                                : 'Registre uma nova internação'}
                        </p>
                    </div>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                    <h3 className="font-bold text-lg mb-2">Hospital</h3>
                    <p className="text-sm text-base-content/60 mb-3">
                        Hospital onde será registrada a internação
                    </p>
                    <HospitalSelector
                        hospitals={hospitals}
                        selectedId={formData.hospitalId || null}
                        onChange={id => {
                            setFormData({ ...formData, hospitalId: id, unitId: 0, bedId: undefined })
                            setSelected(id)
                        }}
                        loading={loadingHospitals}
                    />
                </div>

                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                    <h3 className="font-bold text-lg mb-4">Informações Básicas</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="form-control">
                            <label className="label">
                                <span className="label-text font-medium">Paciente *</span>
                            </label>
                            <input
                                type="number"
                                className="input input-bordered"
                                placeholder="ID do Paciente"
                                value={formData.clientId || ''}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        clientId: parseInt(e.target.value) || 0,
                                    })
                                }
                                required
                            />
                            <label className="label">
                                <span className="label-text-alt text-base-content/60">
                                    Informe o ID do paciente cadastrado
                                </span>
                            </label>
                        </div>

                        <div className="form-control">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Tipo de Internação *
                                </span>
                            </label>
                            <select
                                className="select select-bordered"
                                value={formData.admissionType}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        admissionType: e.target.value as AdmissionType,
                                    })
                                }
                                required
                            >
                                {Object.entries(ADMISSION_TYPE_LABELS).map(
                                    ([key, label]) => (
                                        <option key={key} value={key}>
                                            {label}
                                        </option>
                                    )
                                )}
                            </select>
                        </div>

                        <div className="form-control">
                            <label className="label">
                                <span className="label-text font-medium">Unidade *</span>
                            </label>
                            <select
                                className="select select-bordered"
                                value={formData.unitId}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        unitId: parseInt(e.target.value),
                                        bedId: undefined,
                                    })
                                }
                                required
                            >
                                <option value={0}>Selecione uma unidade</option>
                                {units.map(unit => (
                                    <option key={unit.id} value={unit.id}>
                                        {unit.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="form-control">
                            <label className="label">
                                <span className="label-text font-medium">Leito</span>
                            </label>
                            <select
                                className="select select-bordered"
                                value={formData.bedId || 0}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        bedId:
                                            parseInt(e.target.value) || undefined,
                                    })
                                }
                                disabled={!formData.unitId}
                            >
                                <option value={0}>Selecione um leito</option>
                                {beds.map(bed => (
                                    <option key={bed.id} value={bed.id}>
                                        Leito {bed.number}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="form-control">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Data de Internação *
                                </span>
                            </label>
                            <input
                                type="date"
                                className="input input-bordered"
                                value={formData.admissionDate}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        admissionDate: e.target.value,
                                    })
                                }
                                required
                            />
                        </div>

                        <div className="form-control">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Previsão de Alta
                                </span>
                            </label>
                            <input
                                type="date"
                                className="input input-bordered"
                                value={formData.expectedDischargeDate}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        expectedDischargeDate: e.target.value,
                                    })
                                }
                            />
                        </div>
                    </div>
                </div>

                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                    <h3 className="font-bold text-lg mb-4">Diagnóstico</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="form-control md:col-span-2">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Diagnóstico de Internação
                                </span>
                            </label>
                            <textarea
                                className="textarea textarea-bordered h-24"
                                placeholder="Descrição do diagnóstico..."
                                value={formData.admissionDiagnosis}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        admissionDiagnosis: e.target.value,
                                    })
                                }
                            />
                        </div>

                        <div className="form-control">
                            <label className="label">
                                <span className="label-text font-medium">CID-10</span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered"
                                placeholder="Ex: J18.9"
                                value={formData.cid10Code}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        cid10Code: e.target.value,
                                    })
                                }
                            />
                        </div>

                        <div className="form-control">
                            <label className="label">
                                <span className="label-text font-medium">Alergias</span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered"
                                placeholder="Alergias conhecidas"
                                value={formData.allergies}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        allergies: e.target.value,
                                    })
                                }
                            />
                        </div>
                    </div>
                </div>

                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                    <h3 className="font-bold text-lg mb-4">Isolamento</h3>
                    <div className="space-y-4">
                        <div className="form-control">
                            <label className="label cursor-pointer justify-start gap-4">
                                <input
                                    type="checkbox"
                                    className="checkbox checkbox-warning"
                                    checked={formData.requiresIsolation}
                                    onChange={e =>
                                        setFormData({
                                            ...formData,
                                            requiresIsolation: e.target.checked,
                                        })
                                    }
                                />
                                <div>
                                    <span className="label-text font-medium">
                                        Requer Isolamento
                                    </span>
                                    <p className="text-xs text-base-content/60">
                                        Paciente necessita de precauções especiais
                                    </p>
                                </div>
                            </label>
                        </div>

                        {formData.requiresIsolation && (
                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Motivo do Isolamento
                                    </span>
                                </label>
                                <input
                                    type="text"
                                    className="input input-bordered"
                                    placeholder="Motivo do isolamento"
                                    value={formData.isolationReason}
                                    onChange={e =>
                                        setFormData({
                                            ...formData,
                                            isolationReason: e.target.value,
                                        })
                                    }
                                />
                            </div>
                        )}
                    </div>
                </div>

                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                    <h3 className="font-bold text-lg mb-4">Acompanhante</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="form-control">
                            <label className="label">
                                <span className="label-text font-medium">Nome</span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered"
                                placeholder="Nome do acompanhante"
                                value={formData.companionName}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        companionName: e.target.value,
                                    })
                                }
                            />
                        </div>

                        <div className="form-control">
                            <label className="label">
                                <span className="label-text font-medium">Telefone</span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered"
                                placeholder="(00) 00000-0000"
                                value={formData.companionPhone}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        companionPhone: e.target.value,
                                    })
                                }
                            />
                        </div>

                        <div className="form-control">
                            <label className="label">
                                <span className="label-text font-medium">Parentesco</span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered"
                                placeholder="Ex: Mãe, Filho, Cônjuge"
                                value={formData.companionRelation}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        companionRelation: e.target.value,
                                    })
                                }
                            />
                        </div>
                    </div>
                </div>

                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                    <h3 className="font-bold text-lg mb-4">Observações</h3>
                    <div className="form-control">
                        <textarea
                            className="textarea textarea-bordered h-24"
                            placeholder="Observações adicionais..."
                            value={formData.notes}
                            onChange={e =>
                                setFormData({ ...formData, notes: e.target.value })
                            }
                        />
                    </div>
                </div>

                <div className="flex justify-end gap-2">
                    <button
                        type="button"
                        className="btn btn-ghost"
                        onClick={() => navigate('/hospital/admissions')}
                    >
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        className="btn btn-secondary gap-2"
                        disabled={saving}
                    >
                        {saving ? (
                            <span className="loading loading-spinner loading-sm"></span>
                        ) : (
                            <MdSave className="text-xl" />
                        )}
                        {isEditing ? 'Atualizar' : 'Criar'}
                    </button>
                </div>
            </form>
        </div>
    )
}
